// Microsoft Graph thumbnail fetcher for Contact headshots.
//
// SharePoint auto-generates thumbnails for image files at three preset sizes
// (small ~96px, medium ~176px, large ~800px). The Graph thumbnails endpoint
// returns short-lived (~60 min) pre-signed CDN URLs that can be dropped
// directly into <img src>, no auth header required at the CDN.
//
// Cache strategy:
//   - localStorage-backed so it survives pane reloads. Reusing the exact
//     same SAS URL means the browser's HTTP cache hits for the JPEG bytes
//     across reloads — no re-download per email open.
//   - 45 min TTL, comfortably under SharePoint's ~60 min SAS lifetime.
//   - SAS URLs are bearer-equivalent for their ~1hr lifetime; storing
//     them in localStorage widens the exposure window vs memory-only.
//     Same trust model as the EPIC + Graph access tokens we already
//     keep there.

import { GRAPH_API_BASE } from "../../auth/config";

export type ThumbnailSize = "small" | "medium" | "large";

// Preset sizes confirmed via tinker against the Contacts drive:
//   small    72 ×  96
//   medium  133 × 176
//   large   196 × 260

interface CacheEntry {
  url: string;
  expiresAt: number;
}

const STORAGE_PREFIX = "gp:";
const CACHE_TTL_MS = 45 * 60 * 1000;

const INFLIGHT = new Map<string, Promise<string | null>>();

function cacheKey(driveId: string, itemId: string, size: ThumbnailSize): string {
  return `${driveId}:${itemId}:${size}`;
}

function readCache(key: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (typeof parsed?.url !== "string" || typeof parsed?.expiresAt !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(key: string, entry: CacheEntry): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exceeded, localStorage disabled, etc. — silently drop;
    // we still return the URL to the caller for the current render.
  }
}

function evictCache(key: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    // ignore
  }
}

/**
 * Resolve the pre-signed CDN URL for a Contact's SharePoint headshot
 * thumbnail. Returns null when the file has no thumbnail, the request
 * fails, or the token is missing. Callers should treat null as
 * "render initials instead".
 */
export async function fetchContactThumbnail(
  driveId: string,
  itemId: string,
  size: ThumbnailSize,
  graphToken: string | null
): Promise<string | null> {
  if (!graphToken) return null;

  const key = cacheKey(driveId, itemId, size);
  const now = Date.now();

  const cached = readCache(key);
  if (cached && cached.expiresAt > now) return cached.url;
  if (cached) evictCache(key);

  const existing = INFLIGHT.get(key);
  if (existing) return existing;

  const p = (async () => {
    try {
      const url = `${GRAPH_API_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/thumbnails/0/${size}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${graphToken}` },
      });
      if (!res.ok) {
        if (res.status !== 404) {
          let detail = "";
          try {
            detail = await res.text();
          } catch {
            // ignore
          }
          console.warn(
            `[graphPhoto] thumbnail fetch ${res.status} for ${driveId}/${itemId} (${size}):`,
            detail || "(no body)"
          );
          await logAvailableThumbnails(driveId, itemId, graphToken);
        }
        return null;
      }
      const body = (await res.json()) as { url?: string };
      if (!body.url) return null;
      writeCache(key, { url: body.url, expiresAt: now + CACHE_TTL_MS });
      return body.url;
    } catch (e: any) {
      console.warn("[graphPhoto] thumbnail fetch failed:", e?.message ?? e);
      return null;
    } finally {
      INFLIGHT.delete(key);
    }
  })();

  INFLIGHT.set(key, p);
  return p;
}

/**
 * Diagnostic: fetch the full thumbnails collection for an item and log
 * every size SharePoint exposes (including its width/height). Helps
 * answer "what CAN I ask for here?" when a specific size key 400s.
 *
 * Each item only needs to be inspected once per session — repeated
 * 400s for the same item don't add more value.
 */
const LISTED = new Set<string>();
async function logAvailableThumbnails(
  driveId: string,
  itemId: string,
  graphToken: string
): Promise<void> {
  if (LISTED.has(itemId)) return;
  LISTED.add(itemId);
  try {
    const res = await fetch(
      `${GRAPH_API_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/thumbnails`,
      { headers: { Authorization: `Bearer ${graphToken}` } }
    );
    const text = await res.text();
    if (!res.ok) {
      console.warn(
        `[graphPhoto] /thumbnails listing also failed ${res.status} for ${itemId}:`,
        text
      );
      return;
    }
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      console.warn(`[graphPhoto] /thumbnails non-JSON for ${itemId}:`, text);
      return;
    }
    const sets: any[] = parsed?.value ?? [];
    if (sets.length === 0) {
      console.warn(
        `[graphPhoto] /thumbnails returned empty set for ${itemId} — SharePoint hasn't generated any thumbnails for this file. Library may not support thumbnails, or the file may be too new / not an image.`
      );
      return;
    }
    for (const set of sets) {
      const sizes = Object.keys(set).filter((k) => k !== "id");
      const summary = sizes.map((k) => {
        const v = set[k];
        return v?.width && v?.height
          ? `${k}=${v.width}x${v.height}`
          : k;
      });
      console.info(
        `[graphPhoto] available sizes for ${itemId} (set id=${set.id}):`,
        summary.join(", ") || "(none)"
      );
    }
  } catch (e: any) {
    console.warn(
      "[graphPhoto] listing diagnostic threw:",
      e?.message ?? e
    );
  }
}
